import React, { useState } from 'react';

import { ItemCard, Timeline, CollapsibleContent, DateTimeUtils, ControlledPopup, productRestriction, PRODUCT_PERMISSIONS, USER_PERMISSIONS, BannedIcon } from 'dyl-components';
import { Menu, Form, Icon, Checkbox, Header } from 'semantic-ui-react';

import { PhoneUtil, StringUtils } from 'utils';

import './index.scss';
import { Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ActivityPopup } from 'shared/forms/NewActivity';
import TextIcon from 'shared/TextIcon';
import RestrictedView from 'shared/RestrictedView';

const SequenceTimeline = ({ control, sequenceOptions, onSave, onRemove, onComplete, onCancel, selectedSequenceTasks, selectedSequenceId, selectedSequenceName, clearSequence, canApplySequence, onSelectSequenceTaskForRescheduling, handleOnTextConfirm }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState('Upcoming');
    const [activePopupId, setActivePopupId] = useState();
    const { contact_id } = useParams();
    const productPermissions = useSelector((state) => state.subscription.productPermissions);
    const hasText = productRestriction(PRODUCT_PERMISSIONS.TEXT_VIEW, productPermissions);
    const dispatch = useDispatch();

    const { isAddingContactToSequence, officeViewCreds, contact } = useSelector(state => ({
        isAddingContactToSequence: state.sequences.isAddingContactToSequence,
        officeViewCreds: state.officeView.creds,
        contact: state.contact.contact,
    }))


    const isPhoneSystemSupported =
            PhoneUtil.isPhoneSystemSupported(officeViewCreds);

    const {
        mainPhone,
        call_suspended,
        account_id,
        hasPhone,
        hasEmail
    } = useSelector(state => {
        const ph = state.contact_phone.phone?.find?.(item => item.main);
        const mainPhone = ph?.phone;
        const hasPhone = state.contact?.phone?.some?.(
            (p) => p?.phone && p?.phone?.trim?.()?.length > 0,
        );
        const hasEmail = state.contact?.email?.some?.(
            (e) => e?.email && e?.email?.trim?.()?.length > 0,
        );
        return {
            hasEmail,
            hasPhone,
            mainPhone,
            call_suspended: ph?.call_suspended,
            account_id: state.contact.account_id
        }
    });
    const isUnsubscribedFromTexting = useSelector((state) => state.text.unsubscribedPhones?.includes(mainPhone));

    const getIconClassName = (action) => {
        const actionText = action.split(" ")[0];
        switch (actionText) {
            case "Call":
                return "fa-solid fa-phone"
            case "Email":
                return "fa-solid fa-envelope"
            default:
                return "";
        }
    }

    const getOnClickEvent = (task) => {
        const actionText = task.action.split(" ")[0];
        switch (actionText) {
            case "Call":
                return () => {
                    PhoneUtil.C2D({ payload: { destination: mainPhone, account_id, contact_id: Number(contact_id), requestor_id: task?.id, call_type: task.action_data.callback ? "callback" : "sequence" }, dispatch });
                }
            case "Email":
                return () => {}
            default:
                return () => {};
        }
    }

    const getIsDisabled = (action) => {
        const actionText = action.split(" ")[0];
        switch (actionText) {
            case "Call":
                return !mainPhone || !hasPhone
            case "Email":
                return !hasEmail
            default:
                return false;
        }
    }

    const handleCheckboxChange = (id) => {
        onComplete(id);
    }

    const handleOnCancel = (id) => {
        onCancel(id);
    }

    const mapTasks = (task) => {
        const isComplete = task.status === "completed";
        
        return ({
            date: (
                <div className='TimelineItem__date'>
                    <span className='TimelineItem__day'>
                        {DateTimeUtils.formatEpoch(task.ts, "ddd").toUpperCase()}
                    </span>
                    <span className='TimelineItem__day-of-month'>
                        {DateTimeUtils.formatEpoch(task.ts, "M[/]D")}
                    </span>
                </div>
            ),
            time: (
                <span className='TimelineItem__time'>
                    {DateTimeUtils.formatEpoch(task.ts)}
                </span>
            ),
            itemComponent: (
                <ItemCard
                    key={task?.id}
                    title={<div>
                        <span className='EventItem__name'>
                            {StringUtils.capitalize(selectedSequenceName)}
                        </span>
                    </div>}
                    description={<div className='EventItem__note'>
                        <CollapsibleContent
                            content={task.action || ''} />
                    </div>}
                    actions={<div style={{ display: "flex", gap: 10 }}>
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.CONTACTS_EDIT} userRestrict assignmentIds={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}><Checkbox className="SequenceTime__taskCheckbox" style={{ marginRight: 6 }} checked={isComplete} disabled={isComplete || !hasPhone} onChange={() => handleCheckboxChange(task.id)} /></RestrictedView>
                        {!isPhoneSystemSupported && task?.action?.toLowerCase?.()?.includes?.("call") ? <ActivityPopup
                            trigger={<Icon
                            style={{ marginTop: 2 }}
                                className='fa-solid fa-phone'
                                color="primary"
                                size='large'
                                link
                            />}
                            activityProps={{
                                hideActivitySelection: true,
                                isContact: true,
                                contact: { id: contact_id }
                            }}
                            open={activePopupId === task?.id}
                            onOpen={() => setActivePopupId(task?.id)}
                            onClose={() => setActivePopupId(null)}
                            position="left center"
                        /> :

                            task.action.split(" ")[0] === "Text" ? <ControlledPopup
                                header={
                                    <Header as='h4' textAlign='center'>
                                        Text will be sent to recipient.
                                    </Header>
                                }
                                trigger={
                                    <div>
                                        <TextIcon 
                                            contact_id={contact_id}
                                            account_id={account_id}
                                            primary
                                            phone={mainPhone}
                                            size='large'
                                            style={{ marginTop: 2 }}
                                            disabled={isComplete || !hasPhone}
                                            isActivityFormOpen={activePopupId === task?.id}
                                            onOpenActivityForm={() => setActivePopupId(task?.id)}
                                            onCloseActivityForm={() => setActivePopupId(null)}
                                            position='bottom right'
                                        />
                                    </div>
                                }
                                onConfirm={() => {
                                    handleOnTextConfirm(task)
                                }}
                                on="click"
                                inverted
                                position="bottom center"
                                disabled={isComplete || isUnsubscribedFromTexting || !hasText || !mainPhone || !hasPhone}
                            /> : (
                                !(task.action?.toLowerCase()?.includes("call") && call_suspended) ? (
                                    <Icon size='large' link className={`${getIconClassName(task.action)}`} color='primary' style={{ marginTop: 2 }} disabled={getIsDisabled(task.action)} onClick={getOnClickEvent(task)} />
                                ) : (
                                    <BannedIcon 
                                        iconName={getIconClassName(task.action)}
                                        banIconSize='tiny'
                                        size={"large"}
                                        primary
                                    />
                                )
                            )}
                        <Icon size='large' link className='fa-solid fa-calendar' onClick={() => { onSelectSequenceTaskForRescheduling(task.id); }} color='primary' style={{ marginTop: 2 }} />
                        <Icon size='big' link className='fa-solid fa-xmark redIcon' disabled={isComplete} onClick={() => handleOnCancel(task.id)} />
                    </div>}
                    size='mini' />
            ),
            markerColor: '#e5e5e5'
        });
    }

    const handleTabClick = (e, { name }) => {
        setActiveTab(name);
    }

    const handleCancel = () => {
        clearSequence();
        setIsEditing(false)
    }

    const numberOfCompletedTasks = selectedSequenceTasks?.filter((task) => task.status === 'completed')?.length || 0;

    return (
        <div>
            <Form size='small'>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                    <Controller
                        name='sequence'
                        control={control}
                        render={({ field: { name, value, onChange } }) => (
                            <Form.Select
                                placeholder="Select Sequence Name"
                                label="Sequence Name"
                                name={name}
                                value={value}
                                options={sequenceOptions}
                                onChange={(_, { value }) => {
                                    onChange({ target: { name, value } });
                                }}
                                required
                                search
                                width={13}
                                disabled={selectedSequenceId || sequenceOptions.length === 0 || !isEditing || isAddingContactToSequence}
                            />
                        )}
                    />
                    {sequenceOptions.length > 0 && !isEditing && !selectedSequenceId && <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.CONTACTS_EDIT} userRestrict assignmentIds={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}><Icon name='pencil' color='primary' link onClick={() => setIsEditing(true)} style={{ marginLeft: 10, marginTop: 10 }} /></RestrictedView>}
                    {!isAddingContactToSequence && isEditing && !selectedSequenceId && <>
                        <Icon size='large' link className='fa-solid fa-xmark redIcon' style={{ marginLeft: 10, marginTop: 10 }} onClick={handleCancel} />
                        <Icon disabled={!canApplySequence} size='large' color='primary' link className='fa-solid fa-check' style={{ marginTop: 10 }} onClick={onSave} />
                    </>}
                    {isAddingContactToSequence && <Icon name='circle notched' loading />}
                    {selectedSequenceId && <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.CONTACTS_EDIT} userRestrict assignmentIds={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}><Icon color='primary' link className='fa-solid fa-trash redIcon' style={{ marginLeft: 10, marginTop: 10 }} onClick={onRemove} /></RestrictedView>}
                </div>
            </Form>
            <span>{selectedSequenceId ? `${numberOfCompletedTasks}/${selectedSequenceTasks.length} Tasks Completed` : 'No sequence tasks to display'}</span>
            {sequenceOptions.length > 0 && selectedSequenceId && (
                <>
                    <Menu size='small' borderless pointing secondary color={"primary"} fluid widths={"2"} className="ContactSequence__Menu">
                        <Menu.Item link active={activeTab === "Upcoming"} name='Upcoming' onClick={handleTabClick} />
                        <Menu.Item link active={activeTab === "Overdue"} name='Overdue' onClick={handleTabClick} />
                    </Menu>
                    <Timeline
                        className="SequenceTimeline__divider"
                        items={selectedSequenceTasks?.filter(task => task?.status?.toLowerCase() === activeTab?.toLowerCase()).map(mapTasks) || []}
                        icon={
                            <Icon className='fa-solid fa-sitemap' style={{ margin: 0 }} />
                        }
                    />
                </>
            )}
        </div>
    )
}

export default SequenceTimeline;
